// frontend/src/components/masters/SupplierList.js
import React, { useState, useEffect } from 'react';
import SupplierForm from './SupplierForm';
import useSortableTable from '../common/useSortableTable';
import './SupplierList.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

// Placeholder API endpoint - replace with your actual Django API endpoint
const API_BASE_URL = '/api/masters/suppliers/'; // Added trailing slash

// Helper to get auth token (re-used from other forms/lists)
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json'
});

const SupplierList = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSupplier, setCurrentSupplier] = useState(null); // Supplier data for edit/view
    const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'
    const [searchTerm, setSearchTerm] = useState('');
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Fetch data from Django backend
    const fetchSuppliers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_BASE_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to fetch suppliers');
            }
            const data = await response.json();
            // Assign a unique temporary ID if not already present, useful for lists before saving
            const processedData = data.map(item => ({ ...item, id: item.id || crypto.randomUUID() }));
            setSuppliers(processedData);
        } catch (err) {
            console.error("Error fetching suppliers:", err);
            setError("Failed to load suppliers: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppliers();
    }, []);

    const handleAddSupplier = () => {
        setCurrentSupplier(null); // Clear any previous data
        setFormMode('add');
        setIsModalOpen(true);
    };

    const handleEditSupplier = (supplier) => {
        setCurrentSupplier(supplier);
        setFormMode('edit');
        setIsModalOpen(true);
    };

    const handleViewSupplier = (supplier) => {
        setCurrentSupplier(supplier);
        setFormMode('view');
        setIsModalOpen(true);
    };

    const handleDeleteSupplier = async (id) => {
        if (window.confirm("Are you sure you want to delete this supplier?")) { // Use custom modal in real app
            try {
                const response = await fetch(`${API_BASE_URL}${id}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to delete supplier');
                }
                setSuppliers(prev => prev.filter(s => s.id !== id));
                // alert("Supplier deleted successfully!"); // Use custom modal for success
            } catch (err) {
                console.error("Error deleting supplier:", err);
                setError("Failed to delete supplier: " + err.message);
            }
        }
    };

    const handleFormClose = (refresh = false) => {
        setIsModalOpen(false);
        setCurrentSupplier(null); // Clear current supplier data
        if (refresh) {
            fetchSuppliers(); // Re-fetch data if a save operation occurred
        }
    };

    const getFilteredSuppliers = () => {
        return [...suppliers]
            .filter(supplier =>
                Object.values(supplier).some(value =>
                    String(value).toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
    };

    const { items: sortedSuppliers, requestSort, getSortIndicator } = useSortableTable(getFilteredSuppliers());

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    return (
        <div className="supplier-list-container">
            <h2>Suppliers List</h2>

            {loading && <p className="loading-message">Loading suppliers...</p>}
            {error && <p className="error-message">{error}</p>}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Search suppliers..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {/* 3. Protect Add button */}
                {hasPermission('SUPPLIERS', 'can_create') && (
                    <button onClick={handleAddSupplier}>Add New Supplier</button>
                )}
            </div>

            {!loading && !error && (
                sortedSuppliers.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('name')} className="sortable">
                                    Name {getSortIndicator('name')}
                                </th>   
                                <th onClick={() => requestSort('country')} className="sortable">
                                    Country {getSortIndicator('country')}
                                </th>                             
                                 <th onClick={() => requestSort('city')} className="sortable">
                                    City {getSortIndicator('city')}
                                </th>                                
                                {/* 4. Protect Actions column header */}
                                {(hasPermission('SUPPLIERS', 'can_view') || hasPermission('SUPPLIERS', 'can_edit') || hasPermission('SUPPLIERS', 'can_delete')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSuppliers.map(supplier => (
                                <tr key={supplier.id}>
                                    <td>{supplier.name}</td>                                    
                                    <td>{supplier.country}</td>
                                    <td>{supplier.city}</td>
                                    {/* 5. Protect Actions cell */}
                                    {(hasPermission('SUPPLIERS', 'can_view') || hasPermission('SUPPLIERS', 'can_edit') || hasPermission('SUPPLIERS', 'can_delete')) && (
                                        <td className="actions">
                                            {hasPermission('SUPPLIERS', 'can_view') && (
                                                <button className="icon-btn view-btn" onClick={() => handleViewSupplier(supplier)} title="View">
                                                    <i className="fa fa-eye"></i>
                                                </button>
                                            )}
                                            {hasPermission('SUPPLIERS', 'can_edit') && (
                                                <button className="icon-btn edit-btn" onClick={() => handleEditSupplier(supplier)} title="Edit">
                                                    <i className="fa fa-pencil-alt"></i>
                                                </button>
                                            )}
                                            {hasPermission('SUPPLIERS', 'can_delete') && (
                                                <button className="icon-btn delete-btn" onClick={() => handleDeleteSupplier(supplier.id)} title="Delete">
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-data">No suppliers found.</p>
                )
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
                            &times;
                        </button>
                        <SupplierForm
                            mode={formMode}
                            initialData={currentSupplier}
                            onClose={handleFormClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierList;
